import { useEffect, useMemo, useState } from 'react';
import { Business, Service, ServiceBooking, ServiceOrder } from '@turizi/shared';
import { useSaveServiceOrderBooking, useServices } from '@/hooks/api-hooks';
import { Alert, Loader, Button, TextInput, ActionIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconAlertCircle, IconCheck, IconFlagCog, IconSearch, IconX } from '@tabler/icons-react';
import CardModal from '../card-modal';
import { TagIcon } from '@/lib/tag-utils';
import SearchInput from '../search-input';

interface Props {
    opened: boolean;
    onClose: () => void;
    onCreate?: (booking: ServiceBooking) => void;
    serviceOrder: ServiceOrder;
}

export default function SelectServiceCreateBookingOrder({
    opened,
    onClose,
    onCreate,
    serviceOrder
}: Props) {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
    const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
    const [serviceSearch, setServiceSearch] = useState('');
    const [providerSearch, setProviderSearch] = useState('');
    const [step, setStep] = useState<'category' | 'service' | 'provider'>('category');

    const [error, setError] = useState('');
    const [bookingCreated, setBookingCreated] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    const {
        data: servicesData,
        isLoading: isLoadingServices
    } = useServices(
        {},
        { enabled: opened }
    );
    const createBookingMutation = useSaveServiceOrderBooking();

    const servicesList = useMemo(() => servicesData?.data || [], [servicesData]);

    const categories = useMemo(() => {
        const cats = servicesList.map((s: Service) => s.category).filter(Boolean);
        return Array.from(new Set(cats));
    }, [servicesList]);

    const filteredServices = useMemo(() => {
        if (!selectedCategory) return [];
        return servicesList.filter((s: Service) => s.category === selectedCategory);
    }, [selectedCategory, servicesList]);

    const displayedServices = useMemo(() => {
        if (!serviceSearch) return filteredServices;
        return filteredServices.filter((s: Service) =>
            s.name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
            s.id.toString() === serviceSearch
        );
    }, [filteredServices, serviceSearch]);

    const selectedService = useMemo(() => {
        if (!selectedServiceId) return null;
        return servicesList.find((s: Service) => s.id.toString() === selectedServiceId) || null;
    }, [selectedServiceId, servicesList]);

    const displayedProviders = useMemo(() => {
        if (!selectedService || !selectedService.providers) return [];
        if (!providerSearch) return selectedService.providers;
        return selectedService.providers.filter((p: Business) =>
            p.name.toLowerCase().includes(providerSearch.toLowerCase()) ||
            p.id.toString() === providerSearch
        );
    }, [selectedService, providerSearch]);

    // Automatically set provider if there's only one
    useEffect(() => {
        if (selectedService) {
            if (selectedService.providers && selectedService.providers.length === 1) {
                setSelectedProviderId(selectedService.providers[0].id.toString());
            } else {
                setSelectedProviderId(null);
            }
        } else {
            setSelectedProviderId(null);
        }
    }, [selectedService]);

    // Reset everything when modal is opened or closed
    useEffect(() => {
        if (opened) {
            setSelectedCategory(null);
            setSelectedServiceId(null);
            setSelectedProviderId(null);
            setServiceSearch('');
            setProviderSearch('');
            setStep('category');
            setError('');
            setBookingCreated(false);
            setShowLoading(false);
        }
    }, [opened]);

    const handleClose = () => {
        setSelectedCategory(null);
        setSelectedServiceId(null);
        setSelectedProviderId(null);
        setServiceSearch('');
        setProviderSearch('');
        setStep('category');
        setError('');
        setBookingCreated(false);
        setShowLoading(false);
        onClose();
    };

    const handleCreate = (providerIdToSave?: string | null) => {
        if (!selectedServiceId) return;

        setError('');
        setShowLoading(true);
        setBookingCreated(false);

        const finalProviderId = providerIdToSave !== undefined
            ? providerIdToSave
            : selectedProviderId;

        const payload: Partial<ServiceBooking> = {
            service_id: Number(selectedServiceId),
            service_order_id: serviceOrder.id,
            selected_provider_id: finalProviderId ? Number(finalProviderId) : null,
        };

        createBookingMutation.mutate(payload, {
            onSuccess: (data) => {
                const booking = data?.data;
                setBookingCreated(true);
                setTimeout(() => {
                    onCreate?.(booking);
                    handleClose();
                }, 1000);
            },
            onError: (err: any) => {
                setShowLoading(false);
                setError(t(err.message || 'error_creating_booking'));
            }
        });
    };

    // Rotate loading text
    useEffect(() => {
        if (!showLoading) return;

        const texts = [
            t('checking_availability'),
            t('processing_your_request'),
            t('almost_there'),
            t('finalizing_booking')
        ];

        let index = 0;
        setLoadingText(texts[0]);

        const interval = setInterval(() => {
            index = (index + 1) % texts.length;
            setLoadingText(texts[index]);
        }, 2000);

        return () => clearInterval(interval);
    }, [showLoading, t]);

    return (
        <>
            {/* Loading Modal */}
            <CardModal
                opened={showLoading}
                onClose={handleClose}
                size="lg"
                header={
                    selectedService ? (
                        <div className="flex items-center gap-2">
                            <TagIcon name={selectedService.category} size="xs" noBackground />
                            <span className="font-semibold text-gray-900">{selectedService.name}</span>
                        </div>
                    ) : null
                }
            >
                <div className="space-y-2">
                    {!error && !bookingCreated && (
                        <div className="flex flex-col items-center justify-center py-8">
                            <Loader className="mb-4" color="black" />
                            <p className="text-gray-600 animate-pulse">
                                {loadingText}
                            </p>
                        </div>
                    )}
                    {bookingCreated && (
                        <Alert icon={<IconCheck size={16} />} color="green" variant="light">
                            {t('booking_successfully_created')}
                        </Alert>
                    )}
                </div>
            </CardModal>

            {/* Selection Modal */}
            <CardModal
                opened={opened && !showLoading}
                onClose={handleClose}
                size="lg"
                header={
                    <div className="flex items-center gap-2">
                        <IconFlagCog size={20} className="text-gray-700" />
                        <span className="text-sm font-semibold text-gray-900 leading-none">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                                {t('service_order')} #{serviceOrder.id}
                            </span>
                            {t('operational_services')}
                        </span>
                    </div>
                }
            >
                <div className="space-y-6 py-2">
                    {error && (
                        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                            {error}
                        </Alert>
                    )}

                    {/* Step 1: Select Category */}
                    {step === 'category' && (
                        <div className="space-y-4">
                            {isLoadingServices ? (
                                <div className="flex justify-center py-8">
                                    <Loader color="black" />
                                </div>
                            ) : (
                                <div className="grid grid-cols-4 gap-2">
                                    {categories.map((cat) => (
                                        <div
                                            key={cat}
                                            onClick={() => {
                                                setSelectedCategory(cat);
                                                setStep('service');
                                            }}
                                            className="flex flex-col items-center justify-center p-1 border border-gray-200/80 rounded-2xl hover:border-blue-500 hover:bg-blue-50/20 cursor-pointer transition-all hover:scale-[1.02] shadow-sm hover:shadow-md group"
                                        >
                                            <div className="p-3 bg-gray-50 group-hover:bg-blue-50 rounded-2xl border border-gray-100 group-hover:border-blue-100 transition-colors mb-3">
                                                <TagIcon name={cat} size="md" noBackground hideText />
                                            </div>
                                            <div className="font-semibold text-sm text-gray-700 group-hover:text-blue-700 transition-colors text-center capitalize">
                                                {t(cat)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Select Service */}
                    {step === 'service' && selectedCategory && (
                        <div className="space-y-4">
                            {/* Breadcrumbs / Back button */}
                            <div className="flex items-center gap-2 pb-2 border-b border-gray-100 text-xs text-gray-500">
                                <span
                                    className="hover:text-blue-600 cursor-pointer font-medium"
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setStep('category');
                                    }}
                                >
                                    {t('services')}
                                </span>
                                <span>&gt;</span>
                                <span className="font-bold text-gray-800 flex items-center gap-1">
                                    <TagIcon name={selectedCategory} size="xxs" noBackground />
                                </span>
                            </div>
                            <SearchInput
                                onSearch={(text) => setServiceSearch(text)}
                                initialValue={serviceSearch}
                            />
                            <div className="space-y-1">
                                {displayedServices.length > 0 ? (
                                    displayedServices.map((service: Service) => (
                                        <div
                                            key={service.id}
                                            onClick={() => {
                                                setSelectedServiceId(service.id.toString());
                                                setStep('provider');
                                            }}
                                            className="flex items-center justify-between p-3 border border-gray-100 rounded-xl hover:border-blue-400 hover:bg-blue-50/10 cursor-pointer transition-all group"
                                        >
                                            <div className="flex-1">
                                                <div className="font-bold text-sm text-gray-800 group-hover:text-blue-700 transition-colors">
                                                    {service.name}
                                                </div>
                                                {service.providers && service.providers.length > 0 && (
                                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                                        {service.providers.length === 1
                                                            ? service.providers[0].name
                                                            : `${service.providers.length} ${t('providers')}`}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-xxs text-gray-400">
                                                #{service.id}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-400 italic text-center py-4">
                                        {t('no_services_found')}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Select Provider */}
                    {step === 'provider' && selectedCategory && selectedService && (
                        <div className="space-y-4">
                            {/* Breadcrumbs / Back button */}
                            <div className="flex items-center gap-2 pb-2 border-b border-gray-100 text-xs text-gray-500">
                                <span
                                    className="hover:text-blue-600 cursor-pointer font-medium"
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setSelectedServiceId(null);
                                        setStep('category');
                                    }}
                                >
                                    {t('services')}
                                </span>
                                <span>&gt;</span>
                                <span
                                    className="hover:text-blue-600 cursor-pointer font-medium flex items-center gap-1"
                                    onClick={() => {
                                        setSelectedServiceId(null);
                                        setStep('service');
                                    }}
                                >
                                    <TagIcon name={selectedCategory} size="xxs" noBackground />
                                </span>
                                <span>&gt;</span>
                                <span className="font-bold text-gray-800 flex items-center gap-1">
                                    {selectedService.name}
                                </span>
                            </div>

                            <div className="space-y-4">
                                {selectedService.providers && selectedService.providers.length > 1 && (
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-500 mb-2">{t('select_provider')}:</div>
                                        <SearchInput
                                            onSearch={(text) => setProviderSearch(text)}
                                            initialValue={providerSearch}
                                        />
                                        <div className="space-y-1">
                                            {displayedProviders.length > 0 ? (
                                                displayedProviders.map((provider: Business) => (
                                                    <div
                                                        key={provider.id}
                                                        onClick={() => handleCreate(provider.id.toString())}
                                                        className="flex items-center justify-between p-4 border border-gray-200/80 rounded-2xl hover:border-blue-500 hover:bg-blue-50/20 cursor-pointer transition-all hover:scale-[1.01] shadow-sm group"
                                                    >
                                                        <span className="font-bold text-sm text-gray-700 group-hover:text-blue-700 transition-colors">
                                                            {provider.name}
                                                        </span>
                                                        <span className="text-xxs px-2.5 py-1 bg-gray-50 group-hover:bg-blue-50 border border-gray-100 group-hover:border-blue-100 rounded-md text-gray-500 group-hover:text-blue-600 transition-colors font-semibold">
                                                            {t('select')}
                                                        </span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-xs text-gray-400 italic text-center py-4">
                                                    {t('no_providers_found')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selectedService.providers && selectedService.providers.length === 1 && (
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-1">
                                            <div className="text-xxs text-gray-500 italic">{t('single_provider')}</div>
                                            <div className="font-bold text-sm text-gray-800">
                                                {selectedService.providers[0].name}
                                            </div>
                                        </div>
                                        <Button
                                            color="black"
                                            radius="xl"
                                            size="md"
                                            className="w-full"
                                            onClick={() => handleCreate(selectedService.providers[0].id.toString())}
                                            loading={createBookingMutation.isPending}
                                        >
                                            {t('add')}
                                        </Button>
                                    </div>
                                )}

                                {(!selectedService.providers || selectedService.providers.length === 0) && (
                                    <div className="space-y-4">
                                        <div className="text-xs text-gray-400 italic text-center py-4 bg-gray-50 border border-gray-100 rounded-xl">
                                            {t('no_providers_available')}
                                        </div>
                                        <Button
                                            color="black"
                                            radius="xl"
                                            size="md"
                                            className="w-full"
                                            onClick={() => handleCreate(null)}
                                            loading={createBookingMutation.isPending}
                                        >
                                            {t('add')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </CardModal>
        </>
    );
}
